import { PurchaseOrderStatus } from './enums/PurchaseOrderStatus.enum';
import { BankStatus } from './enums/BankStatus.enum';
import { CustomerStatus } from './enums/CustomerStatus.enum';
import { GRNStatus, GRNPaymentStatus } from './enums/GRNStatus.enum';
import { PaymentMode, ChequeStatus } from './enums/PaymentMode.enum';
import { OrderStatus } from './enums/OrderStatus.enum';
import { VoucherStatus } from './enums/VoucherStatus.enum';
import { FormEvent, ReactNode } from 'react';

export interface Company {
  id?: number;
  name: string;
}

export interface Role {
  id?: number;
  name: string;
}

export interface User {
  id: string;
  company?: Company;
  firstName: string;
  lastName: string;
  roles?: Role[];
  email: string;
  isActive: boolean;
  isVerified: boolean;
  lastLoggedInDate?: string | Date | null;
}

export interface Vendor {
  id: string;
  company?: { name: string };
  organizationName: string;
  address: string;
  contactPersonName: string;
  contactNo: string;
  email: string;
  isActive: boolean;
}

export interface BasicVendor {
  id: string;
  organizationName: string;
}

export interface Address {
  id: string;
  contactPersonName: string;
  contactNo: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postCode: string;
  country: string;
  isDefault: boolean;
}
export interface RawMaterialCategory {
  id: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
}

export interface BasicRawMaterialCategory {
  id: string;
  name: string;
}

export interface RawMaterial {
  id: number;
  company?: { name: string };
  name: string;
  code: string;
  availableQty: number;
  minLevelQty: number;
  unit: string;
  unitPrice: string;
  isActive: boolean;
  rawMaterialCategoryId?: string | null;
  category?: string | null;
}

export interface Product {
  id: number;
  company?: { name: string };
  name: string;
  displayOrder: number;
  unit: string;
  unitPrice: string;
  availableQty: number;
  isActive: boolean;
}

export interface VoucherType {
  id: string;
  company?: { name: string };
  name: string;
  displayOrder: number;
  description: string;
}

export interface Discount {
  id: string;
  company?: { name: string };
  name: string;
  description?: string;
  percentage?: number;
  defaultValue?: number;
}

export interface Surcharge {
  id: string;
  company?: { name: string };
  name: string;
  description: string;
  percentage?: number;
  defaultValue?: number;
}

export interface BankBasicDetails {
  id: string;
  bankName: string;
  accountNumber: string;
}

export interface Bank {
  id: string;
  company?: { name: string };
  bankName: string;
  branchName: string;
  accountName: string;
  accountNumber: string;
  accountType: string;
  status: BankStatus;
}

export interface Customer {
  id: string;
  company?: { name: string };
  name: string;
  customerCode: string;
  organizationName: string;
  telephoneNo: string;
  email: string;
  status: CustomerStatus;
  suspensionReason?: string | null;
}

export interface PurchaseOrder {
  id: string;
  createdAt: string;
  company: { name: string };
  vendor: {
    id: string;
    organizationName: string;
    address: string;
    contactPersonName: string;
    contactNo: string;
    email: string;
    isActive: boolean;
  };
  deliveryAddress: string;
  status: PurchaseOrderStatus;
  preparedBy?: string;
  approvedBy?: string;
  approvedDate?: string | null;
  notes?: string | null;
  purchaseOrderItems: PurchaseOrderItem[];
}

export interface PurchaseOrderItem {
  id: string;
  rawMaterial: { name: string; code: string };
  requestedQty: number;
  approvedQty: number;
}

export interface PurchaseOrderItemResponse {
  id: string;
  rawMaterialId: string;
  name: string;
  requestedQty: number;
  approvedQty: number;
}

export interface PurchaseOrderDisplayItem {
  id: string;
  rawMaterialId: string;
  name: string;
  requestedQty: string | number;
  approvedQty: string | number;
}

export interface PurchaseOrderFormItem {
  id: string;
  rawMaterialId: string;
  requestedQty: string;
}

export interface PurchaseOrderDynamicFieldValues {
  [key: string]: string | number | null | undefined;
}

export interface RawMaterialDetail {
  id: string;
  rawMaterialId: string;
  rawMaterialName: string;
  quantity: number;
}

export interface ProductionMapping {
  id: string;
  productId: string;
  productName: string;
  rawMaterialDetails?: RawMaterialDetail[];
  details?: RawMaterialDetail[];
}

export interface Production {
  id: string;
  productId: string;
  productName: string;
  producedQuantity: number;
  damagedQuantity: number;
  productionDate: string;
  deletedBy: string | null;
  deletedReason: string | null;
}

export const DEFAULT_PURCHASE_ORDER: PurchaseOrder = {
  id: '',
  createdAt: '',
  company: { name: '' },
  vendor: {
    id: '',
    organizationName: '',
    address: '',
    contactPersonName: '',
    contactNo: '',
    email: '',
    isActive: false,
  },
  deliveryAddress: '',
  status: PurchaseOrderStatus.NEW,
  preparedBy: '',
  approvedBy: undefined,
  approvedDate: null,
  notes: '',
  purchaseOrderItems: [],
};

export interface GRN {
  id: string;
  invoiceNumber: string;
  invoiceAmount: number;
  totalPaidAmount: number;
  vendorId: string;
  vendorName: string;
  status: GRNStatus;
  preparedBy: string;
  approvedBy?: string | null;
  approvedDate?: string | null;
  notes?: string | null;
  rejectedNotes?: string | null;
  items?: GRNItem[];
  createdAt: string;
  updatedAt?: string;
}

export interface GRNItem {
  id: string;
  rawMaterialId: string;
  rawMaterialName: string;
  receivedQty: number;
  returnedQty: number;
  unitPrice: number;
}
export interface GRNViewItem {
  id: string;
  rawMaterialName: string;
  receivedQty: number;
  returnedQty: number;
  unitPrice: number;
}
export interface GRNViewData {
  id: string;
  vendorName: string;
  createdAt: string;
  preparedBy: string;
  invoiceNumber: string;
  invoiceAmount: number;
  notes: string;
  status: number;
  approvedDate: string | null;
  approvedBy: string | null;
  rejectedDate: string | null;
  rejectedBy: string | null;
  rejectedNotes: string | null;
  totalPaidAmount: number | undefined;
  items: GRNViewItem[];
}
export interface GRNPayment {
  id: string;
  grnId: string;
  paymentDate: string;
  amount: number;
  paymentMode: PaymentMode;
  receivedBy: string;
  status: GRNPaymentStatus;
  approvedBy?: string | null;
  approvedDate?: string | null;
  notes?: string | null;
  chequeNumber?: string | null;
  chequeDate?: string | null;
  chequeStatus?: ChequeStatus | null;
  chequeRealizedDate?: string | null;
  bankName?: string | null;
  accountNumber?: string | null;
  transactionReference?: string | null;
  createdAt: string;
  updatedAt?: string;
}
export interface GRNFormItem {
  rawMaterialId: string;
  receivedQty: string;
  returnedQty: string;
  unitPrice: string;
  id: string;
  isNew?: boolean;
}

export interface GRNFormValues {
  vendor: string;
  invoiceNumber: string;
  invoiceAmount: string;
  notes: string;
  grnItems: GRNFormItem[];
  [key: string]: string | number | GRNFormItem[] | undefined;
}

export interface GRNItemUpdate {
  rawMaterialId: string;
  receivedQty: number;
  returnedQty: number;
  unitPrice: number;
  id?: string;
}

export interface GRNDynamicFieldValues {
  [key: string]: string | number | undefined;
}

export interface LocationState {
  invoiceNumber?: string;
}

export interface GRNPaymentPayload {
  grnId: string;
  receiverName: string;
  paymentDate: string;
  amount: number;
  paymentMode?: PaymentMode | string;
  chequeNumber?: string;
  chequeDate?: string;
  bankAccountId?: string;
  paymentRefNo?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  orderDate: string;
  scheduledDeliveryDate?: string;
  actualDeliveryDate?: string;
  status: OrderStatus;
  grandTotal: number;
  paidAmount: number;
}

export interface OrderWithCustomer extends Order {
  customerName: string;
}

export interface OrderReportItem extends OrderWithCustomer {
  createdAt: string;
}

export interface ProductSoldReportItem {
  productId: string;
  productName: string;
  quantitySold: number;
  totalRevenue: number;
}

export interface OrderReportResponse {
  orders: OrderReportItem[];
  productsSold?: ProductSoldReportItem[];
  summary: {
    totalOrders: number;
    totalRevenue: number;
    totalPaid: number;
    totalOutstanding: number;
  };
}

export interface ProductReportItem {
  id: number;
  name: string;
  netProduction: number;
  netSales: number;
}

export interface ProductReportResponse {
  products: ProductReportItem[];
  summary: {
    totalProduction: number;
    totalSales: number;
  };
}

export interface CustomerReportItem {
  customerId: string;
  customerName: string;
  noOfOrders: number;
  grandTotal: number;
  paidAmount: number;
  balanceAmount: number;
}

export interface CustomerReportResponse {
  customers: CustomerReportItem[];
  summary: {
    totalOrders: number;
    totalRevenue: number;
    totalBalance: number;
  };
}

export interface CustomerOutstandingReportItem {
  customerId: string;
  customerName: string;
  grandTotal: number;
  paidAmount: number;
  balanceAmount: number;
}

export interface CustomerOutstandingReportResponse {
  customers: CustomerOutstandingReportItem[];
  summary: {
    totalGrandTotal: number;
    totalPaidAmount: number;
    totalOutstanding: number;
  };
}

export interface PaymentReportItem {
  id: string;
  orderId: string;
  paymentDate: string;
  orderNumber: string;
  customerName: string;
  paidAmount: number;
}

export interface PaymentReportResponse {
  payments: PaymentReportItem[];
  summary: {
    totalPaidAmount: number;
  };
}

export interface PaginatedOrderResponse {
  data: OrderWithCustomer[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export interface OrderFilterFormValues {
  customerPicker: string;
  statusPicker: string;
  scheduledStartDate: Date | null;
  scheduledEndDate: Date | null;
  actualStartDate: Date | null;
  actualEndDate: Date | null;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
}

export interface OrderFilterValues {
  customerId: string;
  status: string;
  scheduledStartDate: Date | null;
  scheduledEndDate: Date | null;
  actualStartDate: Date | null;
  actualEndDate: Date | null;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
}

export interface OrderItem {
  id: string;
  productId: string;
  productName?: string;
  quantity: number;
  unitPrice: number;
}

export interface OrderViewItem {
  name: string;
  quantity: number;
  unitPrice: number;
  subTotal: number;
}

export interface OrderViewData {
  id: string;
  isDelivered: boolean;
  orderStatus: string;
  orderNumber: string;
  customerName: string;
  createdAt: string;
  scheduledDeliveryDate: string | null;
  actualDeliveryDate: string | null;
  deliveryNote?: string;
  approvedDate?: string | null;
  preparedBy?: string | null;
  approvedBy?: string | null;
  rejectionNotes?: string | null;
  billingAddress: Address;
  deliveryAddress: Address;
  items: OrderViewItem[];
  discount: {
    name: string;
    amount: number;
    percentage: number;
  };
  surcharges: number;
  grandTotal: number;
}

export interface DeliveryUpdatePayload {
  id: string;
  scheduledDeliveryDate?: string | null;
  deliveryNotes?: string | null;
  isDelivered: boolean;
  actualDeliveryDate?: string | null;
}

// Voucher related interfaces
export interface Voucher {
  id: string;
  createdAt: string;
  payee: string;
  billNumber: string;
  billAmount: number;
  totalPaidAmount: number;
  voucherType: string;
  status: VoucherStatus;
  description: string;
  preparedBy: string;
  approvedBy: string | null;
  approvedDate: string | null;
  rejectionNotes: string | null;
}

export interface PaginatedVoucherResponse {
  data: Voucher[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export interface VoucherFilterFormValues {
  voucherTypePicker: string;
  statusPicker: string;
  startDate: Date | null;
  endDate: Date | null;
}

export interface VoucherFilterValues {
  voucherTypeId: string;
  status: string;
  startDate: Date | null;
  endDate: Date | null;
}

export interface VoucherViewData {
  id: string;
  voucherType: string;
  voucherTypeId: string;
  payee: string;
  description: string;
  preparedBy: string;
  approvedBy: string | null;
  approvedDate: string | null;
  rejectionNotes: string | null;
  billNumber: string;
  billAmount: number;
  totalPaidAmount: number;
  status: VoucherStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ProductionDialogProps {
  height?: string;
  width?: string;
  open: boolean;
  handleClose: () => void;
  title: string;
  subtitle: string;
  formTitle?: string;
  children: ReactNode;
  onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
  smScreen?: boolean;
  buttonWidth?: string;
  buttonLabel?: string;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
}

export interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  idPrefix?: string;
}

export interface DashboardSummaryData {
  totalOrders: number;
  revenue: number;
  received: number;
  outstanding: number;
  production: number;
  expenses: number;
}

export interface ChartDataPoint {
  name?: string; // used for orderStatus
  date?: string; // used for revenueVsOutstanding
  value?: number;
  revenue?: number;
  outstanding?: number;
}

export interface DashboardRecentOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  createdAt: string;
  status: string;
  grandTotal: number;
  balance: number;
}

export interface DashboardTopCustomer {
  customerName: string;
  noOfOrders: number;
  totalAmount: number;
  paid: number;
}

export interface DashboardSupplierPayment {
  vendorName: string;
  invoiceNumber: string;
  paid: number;
  balance: number;
}

export interface DashboardRecentVoucher {
  payee: string;
  type: string;
  amount: number;
  status: string;
}

export interface DashboardSummaryResponse {
  summary: DashboardSummaryData;
  charts: {
    orderStatus: ChartDataPoint[];
    revenueVsOutstanding: ChartDataPoint[];
  };
  recentOrders: DashboardRecentOrder[];
  topCustomers: DashboardTopCustomer[];
  supplierPayments: DashboardSupplierPayment[];
  recentVouchers: DashboardRecentVoucher[];
}
